import React, { useState } from 'react';

import { FormControlLabel, FormGroup, FormLabel, OutlinedInput, Radio, RadioGroup, styled } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useContainer } from 'unstated-next';

import { UserAccountCreationOptions } from '../../../types/adminConfig';
import AdminSettingsContainer from '../AdminSettingsContainer';
import Accordion from '../components/Accordion';
import { StyledFormControl } from '../styles';
import { AdminControlCheckbox } from '../utils/AdminControlCheckbox';

const StyledFormGroup = styled(FormGroup)`
    margin-left: 15px;
    margin-bottom: 16px;
`;

const StyledInput = styled(OutlinedInput)`
    margin-left: 3rem;
    margin-right: 1rem;
    margin-bottom: 1rem;
    width: 32rem;
`;

const UserAccountCreationSetting = () => {
    const { adminSettings, setAdminSettings, setSavingDisabled } = useContainer(AdminSettingsContainer);
    const {
        featureSettings: { options }
    } = adminSettings;
    const { t } = useTranslation('admin_settings');

    const [disableDomainNames, setDisableDomainNames] = useState(
        options.userAccountCreation !== UserAccountCreationOptions.Domains
    );

    const disableSaveButton = () => {
        setSavingDisabled(
            options.userAccountCreation === UserAccountCreationOptions.Domains &&
                (!options.userAccountCreationDomains ||
                    options.userAccountCreationDomains.length === 0 ||
                    options.userAccountCreationDomains.some((d) => !d || d.includes(' ')))
        );
    };

    const handleDomainsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        // Make sure spaces are trimmed from the domains
        options.userAccountCreationDomains = event.target.value.split(',').map((d) => d?.trim().toLowerCase());
        setAdminSettings({ ...adminSettings });
        disableSaveButton();
    };

    const handleUpdateOptionsRadio = (event: React.ChangeEvent<HTMLInputElement>) => {
        options[event.target.name] = event.target.value;
        setAdminSettings({ ...adminSettings });
        setDisableDomainNames(options.userAccountCreation !== UserAccountCreationOptions.Domains);
        disableSaveButton();
    };

    return (
        <Accordion title={t('auth.account_create-and-sign-on')}>
            <StyledFormGroup>
                <FormLabel id="user-account-creation-label">{t('auth.user-account-creation.title')}</FormLabel>
                <RadioGroup
                    aria-labelledby="user-account-creation-label"
                    name="userAccountCreation"
                    value={
                        options.userAccountCreation ??
                        (adminSettings.zoomAppId !== 'teams'
                            ? UserAccountCreationOptions.AdminOnly
                            : UserAccountCreationOptions.AllUsers)
                    }
                    onChange={handleUpdateOptionsRadio}
                >
                    <StyledFormControl>
                        <FormControlLabel
                            value={UserAccountCreationOptions.AdminOnly}
                            control={<Radio />}
                            label={t('auth.user-account-creation.admin')}
                        />
                    </StyledFormControl>
                    <FormControlLabel
                        value={UserAccountCreationOptions.Domains}
                        control={<Radio />}
                        label={t('auth.user-account-creation.domain')}
                    />
                    <AdminControlCheckbox
                        control={
                            <StyledInput
                                value={options.userAccountCreationDomains?.join(', ').trim() ?? ''}
                                onChange={handleDomainsChange}
                                id="domains"
                                name="domains"
                                placeholder={t('auth.user-account-creation.domains-placeholder')}
                                disabled={disableDomainNames}
                            />
                        }
                        label=""
                    />
                    <FormControlLabel
                        value={UserAccountCreationOptions.AllUsers}
                        control={<Radio />}
                        label={t('auth.user-account-creation.anyone')}
                    />
                </RadioGroup>
            </StyledFormGroup>
        </Accordion>
    );
};

export default UserAccountCreationSetting;
